import { create } from "zustand";
import {
  heartbeatPresence,
  listPresence,
  updatePresenceVisibility,
  type PresenceStatus,
  type PresenceVisibility,
} from "@slumbr/core";

const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === "true";
const PRESENCE_TIMEOUT_MS = 6 * 60_000;
const presenceExpiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

interface PresenceStore {
  statuses: Record<string, PresenceStatus>;
  selfStatus: PresenceStatus | null;
  visibility: PresenceVisibility;
  isAppearOffline: boolean;
  setLocalSelfStatus: (status: PresenceStatus) => void;
  setStatus: (status: PresenceStatus) => void;
  setVisibility: (visibility: PresenceVisibility) => Promise<void>;
  heartbeat: (idleSince?: string | null) => Promise<void>;
  fetchPresence: (userIds: string[]) => Promise<void>;
  getStatus: (userId?: string) => PresenceStatus | undefined;
}

function localStatus(userId: string, visibility: PresenceVisibility): PresenceStatus {
  const now = new Date().toISOString();
  return {
    userId,
    visibility,
    effectiveStatus: visibility === "offline" ? "offline" : visibility,
    online: visibility !== "offline",
    lastSeen: now,
    updatedAt: now,
  };
}

function clearPresenceExpiry(userId: string) {
  const timer = presenceExpiryTimers.get(userId);
  if (timer) clearTimeout(timer);
  presenceExpiryTimers.delete(userId);
}

export const usePresenceStore = create<PresenceStore>((set, get) => ({
  statuses: {},
  selfStatus: null,
  visibility: "online",
  isAppearOffline: false,

  setLocalSelfStatus: (status) => set((state) => ({
    selfStatus: status,
    visibility: status.visibility,
    isAppearOffline: status.visibility === "offline",
    statuses: { ...state.statuses, [status.userId]: status },
  })),

  setStatus: (status) => {
    clearPresenceExpiry(status.userId);
    set((state) => ({
      statuses: { ...state.statuses, [status.userId]: status },
    }));

    if (!status.online) return;
    const lastSeenMs = Date.parse(status.lastSeen);
    if (!Number.isFinite(lastSeenMs)) return;

    const delay = Math.max(0, lastSeenMs + PRESENCE_TIMEOUT_MS - Date.now());
    const timer = setTimeout(() => {
      set((state) => {
        const current = state.statuses[status.userId];
        if (!current || current.lastSeen !== status.lastSeen || !current.online) {
          return state;
        }
        return {
          statuses: {
            ...state.statuses,
            [status.userId]: {
              ...current,
              effectiveStatus: "offline",
              online: false,
            },
          },
        };
      });
      presenceExpiryTimers.delete(status.userId);
    }, delay);
    presenceExpiryTimers.set(status.userId, timer);
  },

  setVisibility: async (visibility) => {
    if (DEV_AUTH_BYPASS) {
      get().setLocalSelfStatus(localStatus("dev-user", visibility));
      return;
    }
    const status = await updatePresenceVisibility(visibility);
    get().setLocalSelfStatus(status);
  },

  heartbeat: async (idleSince) => {
    if (DEV_AUTH_BYPASS) return;
    const status = await heartbeatPresence(idleSince);
    get().setLocalSelfStatus(status);
  },

  fetchPresence: async (userIds) => {
    const ids = Array.from(new Set(userIds.filter(Boolean)));
    if (ids.length === 0) return;
    if (DEV_AUTH_BYPASS) return;
    const result = await listPresence(ids);
    for (const item of result.items) get().setStatus(item);
  },

  getStatus: (userId) => userId ? get().statuses[userId] : undefined,
}));
